import { useState, useEffect } from "react";
import {
  Autocomplete,
  DatePicker,
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Divider,
} from "../../../../components";
import {
  ColecoesService,
  TagsService,
  GrupoProdutosService,
  MarcasService,
  SessionService,
} from "../../../../services";
import { InputErros, value } from "../../../../helpers";
import { formatDate, responseErros } from "../../../../utils";
import { useFiltrosRelatoriosHandlers } from "../../components/use-filtros-relatorios-handlers";
import { format } from "date-fns";

const colecoesService = new ColecoesService();
const marcasService = new MarcasService();
const tagsService = new TagsService();
const grupoProdutosService = new GrupoProdutosService();

const FormFiltroAvancadoInventario = ({
  filtrosAvancados,
  setFiltrosAvancados,
  filtrosAplicadosImpressao,
  setFiltrosAplicadosImpressao,
  setValidationError,
}) => {
  const [grupoProdutoList, setGrupoProdutoList] = useState([]);
  const [inputErro, setInputErro] = useState([]);
  const [tags, setTags] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [colecoes, setColecoes] = useState([]);
  const inputErros = new InputErros(inputErro, setInputErro);
  const { onChangeAutocomplete, onChangeCheckbox } =
    useFiltrosRelatoriosHandlers(
      setFiltrosAvancados,
      setFiltrosAplicadosImpressao,
    );
  const listaTipos = [
    {
      label: "Preço de Compra",
      value: "precoCompra",
    },
    {
      label: "Preço de Custo",
      value: "precoCusto",
    },
  ];

  const sessionService = new SessionService();
  const usuario = sessionService.getUsuario();
  const empresa = sessionService.getEmpresa();

  useEffect(() => {
    setEstadoInicial();
    buscarTags();
    buscarMarcas();
    buscarColecoes();
    buscarGruposProduto();
  }, []);

  function setEstadoInicial() {
    const filtros = {
      ...filtrosAvancados,
      empresasIds: [empresa.id],
    };
    let filtrosImpressao = {};
    if (usuario.acessosEmpresas?.length) {
      filtrosImpressao = {
        ...filtrosAplicadosImpressao,
        ["Empresas-Associadas"]: empresa.razaoSocial,
      };
    }

    setFiltrosAvancados(filtros);
    setFiltrosAplicadosImpressao(filtrosImpressao);
  }

  const buscarGruposProduto = () => {
    const filtros = {
      attributes: ['id', 'codigo', 'descricao'],
      disabledInclude: true,
      nonPaginated: true,
    };
    grupoProdutosService.getAll(filtros).then((result) => {
      if (!result.isAxiosError) {
        setGrupoProdutoList(result.data);
      } else {
        responseErros(result);
      }
    });
  };

  const buscarTags = async function () {
    const filtros = {
      nonPaginated: true,
      ativado: true,
      tipo: "PRODUTO",
    };
    const result = await tagsService.getAll(filtros);
    if (!result.isAxiosError) {
      setTags(result.data);
    } else {
      responseErros(result);
    }
  };

  const buscarMarcas = async function (filter) {
    const result = await marcasService.getAll(filter);
    if (!result.isAxiosError) {
      setMarcas(result.data.rows);
    } else {
      responseErros(result);
    }
  };

  const buscarColecoes = async function (filter) {
    const result = await colecoesService.getAll(filter);
    if (!result.isAxiosError) {
      setColecoes(result.data.rows);
    } else {
      responseErros(result);
    }
  };

  const onChangeMultiple = (valores, name) => {
    if (!valores?.length) {
      resetarFiltros(name);
      return;
    }
    setFiltrosAvancados((prevState) => ({
      ...prevState,
      [name]: valores.map(({ id }) => id),
    }));
    setFiltrosAplicadosImpressao((prevState) => ({
      ...prevState,
      [formatChaveFiltroImpressao(name)]: formatValorFiltroImpressao(
        valores,
        name,
      ),
    }));
  };

  function resetarFiltros(name) {
    setFiltrosAvancados((prevState) => {
      const prevStateTemp = { ...prevState };
      delete prevStateTemp[name];
      return prevStateTemp;
    });

    setFiltrosAplicadosImpressao((prevState) => {
      const prevStateTemp = { ...prevState };
      delete prevStateTemp[formatChaveFiltroImpressao(name)];
      return prevStateTemp;
    });
  }

  function formatChaveFiltroImpressao(name) {
    return name.endsWith("Id") ? name.slice(0, -2).toUpperCase() : name;
  }

  function formatValorFiltroImpressao(valores, name) {
    switch (name) {
      case "tagsId":
        return valores.map((valor) => valor.descricao);
      case "marcasId":
        return valores.map((valor) => valor.nome);
      case "colecoesId":
        return valores.map((valor) => valor.descricao);
      default:
        return valores;
    }
  }

  function onChangeDataReferencia(date) {
    const isValidDate = (d) => d instanceof Date && !isNaN(d);
    if (!date || !isValidDate(date)) date = null;

    if (date?.getFullYear() > 2000) {
      const formattedDate = formatDate.toSend(date);
      setFiltrosAvancados((prevState) => ({
        ...prevState,
        porIntervalo: [
          {
            coluna: "updated_at",
            de: new Date("2024-01-01").toISOString(),
            ate: formattedDate,
          },
        ],
      }));

      const formattedDisplay = format(date, "dd/MM/yyyy");
      setFiltrosAplicadosImpressao((prevState) => ({
        ...prevState,
        ["Data-Referência"]: formattedDisplay,
      }));
    }
  }

  function onChangeTipoPreco(tipoPreco) {
    if (!tipoPreco) {
      setFiltrosAvancados((prevState) => {
        const prevStateTemp = { ...prevState };
        delete prevStateTemp.tipoPreco;
        return prevStateTemp;
      });
      setFiltrosAplicadosImpressao((prevState) => {
        const prevStateTemp = { ...prevState };
        delete prevStateTemp["Tipo Preço"];
        return prevStateTemp;
      });
      return;
    }
    setFiltrosAvancados((prevState) => ({
      ...prevState,
      tipoPreco,
    }));
    setFiltrosAplicadosImpressao((prevState) => ({
      ...prevState,
      ["Tipo Preço"]: listaTipos.find(({ value }) => value === tipoPreco)?.label,
    }));
  }

  const getMultipleAutocompleteValues = (valores, lista) => {
    if (valores) {
      return valores.map((valor) => lista.find((item) => item.id === valor));
    }
    return [];
  };

  function onChangeMultipleEmpresas(empresas) {
    if (empresas.length === 0) {
      return;
    }
    setFiltrosAvancados((prevState) => ({
      ...prevState,
      empresasIds: empresas.map(({ id }) => id),
    }));
    setFiltrosAplicadosImpressao((prevState) => ({
      ...prevState,
      "Empresas-Associadas": empresas
        .map(({ razaoSocial }) => razaoSocial)
        .join(" | "),
    }));
  }

  return (
    <>
      {usuario.acessosEmpresas?.length > 0 && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                id="empresasIds"
                name="empresasIds"
                options={usuario.acessosEmpresas}
                noOptionsText="Sem opções"
                autoHighlight
                disablePortal
                filterSelectedOptions
                getOptionLabel={(option) => option?.razaoSocial || ""}
                onChange={(_, newValue) => onChangeMultipleEmpresas(newValue)}
                value={value.multiple(
                  usuario.acessosEmpresas || [],
                  filtrosAvancados["empresasIds"]
                    ? filtrosAvancados["empresasIds"]
                    : "",
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    required
                    label="Empresas"
                    margin="normal"
                  />
                )}
              />
            </Grid>
          </Grid>
          <Divider sx={{ mt: 2, mb: 1, border: "1px solid grey" }} />
        </>
      )}
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <DatePicker
            id="dataReferencia"
            name="dataReferencia"
            label="Data Referência"
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataReferencia)}
            margin="normal"
            onChange={(date) => onChangeDataReferencia(date)}
            slotProps={{
              popper: { sx: { zIndex: 2000 } },
            }}
            setError={setValidationError}
          />
        </Grid>
        <Grid item xs={6}>
          <Select
            label="Tipo Preço"
            id="tipoPreco"
            name="tipoPreco"
            variant="outlined"
            value={value.text(filtrosAvancados?.tipoPreco)}
            margin="normal"
            onChange={(event) => onChangeTipoPreco(event.target.value)}
            SelectProps={{
              MenuProps: {
                disablePortal: true,
                PaperProps: {
                  sx: {
                    zIndex: 1600,
                  },
                },
              },
            }}
          >
            {listaTipos.map((tipo) => (
              <MenuItem key={tipo.value} value={tipo.value}>
                {tipo.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          multiple
          id="tagsId"
          name="tagsId"
          options={tags}
          noOptionsText="Sem opções"
          autoHighlight
          disablePortal
          filterSelectedOptions
          getOptionLabel={(option) => (option ? option.descricao : "")}
          renderOption={(props, option) =>
            option ? (
              <li {...props}>
                <div className="d-flex align-items-center gap-2 w-100">
                  <i
                    className="ph-fill ph-bookmark-simple"
                    style={{ color: option.cor, fontSize: 18 }}
                  />
                  <div>{option.descricao}</div>
                </div>
              </li>
            ) : (
              ""
            )
          }
          onChange={(_, newValue) => onChangeMultiple(newValue, "tagsId")}
          value={getMultipleAutocompleteValues(filtrosAvancados?.tagsId, tags)}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              margin="normal"
              label="Tags"
            />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          multiple
          id="marcasId"
          name="marcasId"
          options={marcas}
          noOptionsText="Sem opções"
          autoHighlight
          disablePortal
          filterSelectedOptions
          getOptionLabel={(option) => option?.nome || ""}
          onChange={(_, newValue) => onChangeMultiple(newValue, "marcasId")}
          value={getMultipleAutocompleteValues(
            filtrosAvancados?.marcasId,
            marcas,
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              margin="normal"
              label="Marcas"
            />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          multiple
          id="colecoesId"
          name="colecoesId"
          options={colecoes}
          noOptionsText="Sem opções"
          autoHighlight
          disablePortal
          filterSelectedOptions
          getOptionLabel={(option) => option?.descricao || ""}
          onChange={(_, newValue) => onChangeMultiple(newValue, "colecoesId")}
          value={getMultipleAutocompleteValues(
            filtrosAvancados?.colecoesId,
            colecoes,
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              margin="normal"
              label="Coleções"
            />
          )}
        />
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl variant="outlined" fullWidth>
            <Autocomplete
              id="grupoProdutoId"
              name="grupoProdutoId"
              options={grupoProdutoList}
              noOptionsText="Sem opções"
              autoHighlight
              disablePortal
              getOptionLabel={(option) => (option ? option.descricao : "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Grupo de Produto"
                  variant="outlined"
                  margin="normal"
                  error={inputErros.get("grupoProdutoId")}
                />
              )}
              onChange={(_, newValue) => {
                setInputErro([]);
                onChangeAutocomplete(
                  "grupoProdutoId",
                  newValue,
                  "Grupo de Produtos",
                );
              }}
              value={value.autoComplete(
                grupoProdutoList,
                filtrosAvancados?.grupoProdutoId,
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <Select
            label="Ordenar Por"
            id="ordenacao"
            name="ordenacao"
            variant="outlined"
            value={value.text(filtrosAvancados?.ordenacao)}
            margin="normal"
            onChange={(event) => {
              setFiltrosAvancados({
                ...filtrosAvancados,
                [event.target.name]: event.target.value ?? null,
              });
            }}
            SelectProps={{
              MenuProps: {
                disablePortal: true,
                PaperProps: {
                  sx: {
                    zIndex: 1600,
                  },
                },
              },
            }}
          >
            <MenuItem value="codigo">Código</MenuItem>
            <MenuItem value="produto">Descrição</MenuItem>
            <MenuItem value="estoque">Estoque</MenuItem>
          </Select>
        </Grid>
      </Grid>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={6}>
          <TextField
            id="numeroPaginaInicial"
            name="numeroPaginaInicial"
            label="Número Página Inicial"
            type="number"
            variant="outlined"
            value={value.text(filtrosAvancados?.numeroPaginaInicial)}
            margin="normal"
            onChange={(event) => {
              const value = event.target.value;
              setFiltrosAvancados((prevState) => ({
                ...prevState,
                numeroPaginaInicial: value ? parseInt(value, 10) : null,
              }));
            }}
            inputProps={{ min: 0 }}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControlLabel
            label="Ignorar Estoque Negativo/Zerado"
            labelPlacement="end"
            control={
              <Checkbox
                name="ignorarEstoqueZero"
                checked={filtrosAvancados?.ignorarEstoqueZero}
                onChange={(event) =>
                  onChangeCheckbox(event, "Ignorar-Estoque-Negativo/Zerado")
                }
                color="primary"
              />
            }
          />
        </Grid>
      </Grid>
    </>
  );
};

export default FormFiltroAvancadoInventario;
